/**
 * UC-04: LaunchProduct
 * TOGAF Application ABB — activate a product when grid infrastructure meets requirements.
 */
import { PRODUCTS } from '../../../constants';
import { BuildingType } from '../../../types';
import type { Grid } from '../../../types';
import type { IAppLogger } from '../../domain/ports/IAppLogger';
import type { LaunchProductInput, LaunchProductOutput } from '../dto/EconomyDto';

export interface LaunchProductDependencies {
  logger?: IAppLogger;
}

export class LaunchProductUseCase {
  constructor(private readonly deps: LaunchProductDependencies = {}) {}

  execute(input: LaunchProductInput): LaunchProductOutput {
    const { productId, grid, stats } = input;
    const product = PRODUCTS.find((p) => p.id === productId);

    if (!product || stats.activeProducts.includes(productId)) {
      return { ok: false, stats, skipped: true };
    }

    const counts = this.countConnectedBuildings(grid);
    const canLaunch = product.requirements.every(
      (req) => (counts[req.type] || 0) >= req.count,
    );

    if (!canLaunch) {
      this.deps.logger?.log('debug', 'product.launch_failed', { productId });
      return {
        ok: false,
        stats,
        error: 'Infrastructure missing or disconnected',
        newsMessage: 'Launch Failed: Infrastructure missing or disconnected.',
        newsType: 'negative',
        sfx: 'error',
      };
    }

    const updatedStats = {
      ...stats,
      activeProducts: [...stats.activeProducts, productId],
    };

    this.deps.logger?.log('debug', 'product.launched', {
      productId,
      productName: product.name,
    });

    return {
      ok: true,
      stats: updatedStats,
      productName: product.name,
      newsMessage: `PRODUCT LAUNCH: ${product.name} is now live!`,
      newsType: 'positive',
      sfx: 'success',
    };
  }

  private countConnectedBuildings(grid: Grid): Record<string, number> {
    const counts: Record<string, number> = {};
    grid.flat().forEach((tile) => {
      if (tile.buildingType !== BuildingType.None && tile.isConnected) {
        counts[tile.buildingType] = (counts[tile.buildingType] || 0) + 1;
      }
    });
    return counts;
  }
}